import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { AREA_LABELS, type Area } from "@/lib/permissions";
import { IconCloudOff, IconLock } from "@tabler/icons-react";
import { Link } from "react-router";

export function NoAccessScreen({
  area,
  title,
}: {
  area: Area;
  title?: string;
}) {
  return (
    <div className="flex h-full flex-col">
      <SiteHeader title={title ?? AREA_LABELS[area]} />
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <IconLock className="size-6" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-semibold">
              You don't have access to {AREA_LABELS[area]}
            </h2>
            <p className="text-sm text-muted-foreground">
              Ask someone with access to Special Users to grant you{" "}
              <span className="font-mono text-xs">{area}</span>.
            </p>
          </div>
          <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ServerUnreachableScreen({
  onRetry,
  onLogout,
}: {
  onRetry: () => void;
  onLogout: () => void;
}) {
  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <IconCloudOff className="size-6" />
        </div>
        <div className="space-y-1">
          <h2 className="text-base font-semibold">Can't reach the server</h2>
          <p className="text-sm text-muted-foreground">
            The admin API isn't responding. Check your connection or that the
            backend is running, then retry.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
          <Button variant="ghost" size="sm" onClick={onLogout}>
            Log out
          </Button>
        </div>
      </div>
    </div>
  );
}

export function NoAdminAccessScreen({ onLogout }: { onLogout: () => void }) {
  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <IconLock className="size-6" />
        </div>
        <div className="space-y-1">
          <h2 className="text-base font-semibold">No admin access</h2>
          <p className="text-sm text-muted-foreground">
            This account isn't granted any admin area. Ask an administrator to
            grant you access, then reload.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
          <Button variant="ghost" size="sm" onClick={onLogout}>
            Log out
          </Button>
        </div>
      </div>
    </div>
  );
}
